import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { useServerFn } from "@tanstack/react-start";
import { submitProviderRequest } from "@/lib/provider-requests.functions";

type RequestType = "report" | "update" | "removal" | "is_this_you";

const TITLES: Record<RequestType, string> = {
 report: "Report this listing",
 update: "Suggest an edit",
 removal: "Request removal",
 is_this_you: "Is this you?",
};

const DESCRIPTIONS: Record<RequestType, string> = {
 report: "Tell us what's inaccurate, outdated, or inappropriate. Our team reviews every report.",
 update: "Suggest a correction to this listing. Verified updates appear within 48 hours.",
 removal: "Request removal of this listing. We'll verify identity before any changes.",
 is_this_you: "Confirm this profile belongs to you. We'll send instructions to verify and claim it.",
};

interface Props {
 providerId: string;
 open: boolean;
 onOpenChange: (v: boolean) => void;
 type: RequestType;
}

export function ProviderTrustFunnel({ providerId, open, onOpenChange, type }: Props) {
 const submit = useServerFn(submitProviderRequest);
 const [name, setName] = useState("");
 const [email, setEmail] = useState("");
 const [message, setMessage] = useState("");
 const [busy, setBusy] = useState(false);

 const onSubmit = async (e: React.FormEvent) => {
 e.preventDefault();
 if (!email || !message) {
 toast.error("Email and message are required.");
 return;
 }
 setBusy(true);
 try {
 await submit({
 data: {
 providerId,
 requestType: type,
 name: name || undefined,
 email,
 message,
 },
 });
 toast.success("Thanks, your request was submitted.");
 setName(""); setEmail(""); setMessage("");
 onOpenChange(false);
 } catch (err) {
 toast.error(err instanceof Error ? err.message : "Submission failed.");
 } finally {
 setBusy(false);
 }
 };

 return (
 <Dialog open={open} onOpenChange={onOpenChange}>
 <DialogContent className="sm:max-w-[460px]">
 <DialogHeader>
 <DialogTitle>{TITLES[type]}</DialogTitle>
 <DialogDescription>{DESCRIPTIONS[type]}</DialogDescription>
 </DialogHeader>
 <form onSubmit={onSubmit} className="space-y-4">
 <div className="space-y-1.5">
 <Label htmlFor="tf-name">Your name</Label>
 <Input id="tf-name" value={name} onChange={(e) => setName(e.target.value)} maxLength={200} />
 </div>
 <div className="space-y-1.5">
 <Label htmlFor="tf-email">Email *</Label>
 <Input id="tf-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} maxLength={255} />
 </div>
 <div className="space-y-1.5">
 <Label htmlFor="tf-message">Details *</Label>
 <Textarea id="tf-message" required rows={4} value={message} onChange={(e) => setMessage(e.target.value)} maxLength={4000} />
 </div>
 <DialogFooter>
 <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
 <Button type="submit" disabled={busy}>{busy ? "Submitting…" : "Submit"}</Button>
 </DialogFooter>
 </form>
 </DialogContent>
 </Dialog>
 );
}
